import {
  ColumnDef,
  ExpandedState,
  OnChangeFn,
  Row,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  getExpandedRowModel,
  useReactTable,
  RowSelectionState,
} from '@tanstack/react-table';
import classnames from 'classnames';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useHeightTransition } from 'hooks/useHeightTransition';
import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';

export interface PratusTableProps<RowT = any> {
  className?: string; // table classes
  headerClassName?: string; // thead classes
  columns: ColumnDef<RowT, any>[];
  data: RowT[];
  getRowId?: (row: RowT, index: number) => string;
  noDataIndication?: string | React.ReactElement;
  footer?: React.ReactElement;
  expansion?: {
    getRowCanExpand?: (row: Row<RowT>) => boolean;
    expandedRowModel?: (row: RowT) => React.ReactElement;
    /**
     * When true, PratusTable will manage the TanStack `expanded` state
     * internally and ensure that at most one row is expanded at a time.
     *
     * When false/omitted, expansion state is left "uncontrolled" and handled
     * entirely by TanStack Table.
     */
    singleExpansion?: boolean;
    /** Optional callback fired _after_ the internal expanded state changes. */
    onExpandedChange?: (nextExpanded: ExpandedState) => void;
    getExpandedRowClassName?: (row: Row<RowT>) => string | undefined;
  };
  selection?: {
    onRowSelectionChange: React.Dispatch<
      React.SetStateAction<RowSelectionState>
    >;
    selectedRows: RowSelectionState;
    enableRowSelection?: (row: Row<RowT>) => boolean;
    enableMultiRowSelection?: boolean;
  };
  getRowClassName?: (row: Row<RowT>) => string | undefined;
  // When true, sorting is considered server/manual: we still show sort UI,
  // but we won't re-order rows client-side.
  manualSorting?: boolean;
  onSortingChange?: (nextSort: SortingState) => void;
  pagination?: {
    page: number; // 1-based
    pageCount: number;
    onPageChange: (page: number) => void;
    perPage?: number;
    perPageOptions?: number[];
    onPerPageChange?: (perPage: number) => void;
  };
  responsive?: boolean; // whether to apply table-responsive class (default: true)
}

const EXPANDED_ROW_ANIMATION_MS = 350;

type ExpandedRowProps<RowT = any> = {
  row: Row<RowT>;
  colSpan: number;
  className?: string;
  expandedRowModel: (row: RowT) => React.ReactElement;
};

function ExpandedRow({
  row,
  colSpan,
  className,
  expandedRowModel,
}: ExpandedRowProps) {
  const isExpanded = row.getIsExpanded();
  const { contentRef, shouldRender } = useHeightTransition({
    isOpen: isExpanded,
    duration: EXPANDED_ROW_ANIMATION_MS,
    unmountOnExit: true,
  });

  if (!shouldRender) return null;

  return (
    <tr>
      <td className={className} colSpan={colSpan}>
        <div
          ref={contentRef}
          style={{
            overflow: 'hidden',
            transition: `height ${EXPANDED_ROW_ANIMATION_MS}ms ease`,
          }}
          aria-hidden={!isExpanded}
        >
          {expandedRowModel(row.original)}
        </div>
      </td>
    </tr>
  );
}

export default function PratusTable({
  className,
  headerClassName,
  columns,
  data,
  getRowId,
  noDataIndication,
  footer,
  expansion,
  selection,
  getRowClassName,
  manualSorting,
  onSortingChange,
  pagination,
  responsive = true,
}: PratusTableProps) {
  const {
    getRowCanExpand,
    expandedRowModel,
    singleExpansion,
    onExpandedChange,
    getExpandedRowClassName,
  } = expansion ?? {};

  const {
    onRowSelectionChange,
    selectedRows,
    enableRowSelection,
    enableMultiRowSelection,
  } = selection ?? {};

  const [sort, setSort] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const isSingleExpansionMode = Boolean(singleExpansion && expandedRowModel);

  const handleExpandedChange: OnChangeFn<ExpandedState> | undefined =
    isSingleExpansionMode
      ? (updater) => {
          setExpanded((prev) => {
            const nextRaw =
              typeof updater === 'function'
                ? (updater as (old: ExpandedState) => ExpandedState)(prev)
                : updater;

            // Enforce a single expanded row: keep only the last truthy entry.
            let lastKey: string | null = null;
            for (const [key, value] of Object.entries(nextRaw)) {
              if (value) lastKey = key;
            }

            const next: ExpandedState = {};
            if (lastKey) {
              (next as Record<string, boolean>)[lastKey] = true;
            }

            onExpandedChange?.(next);
            return next;
          });
        }
      : undefined;

  const state = {
    sorting: sort,
    ...(isSingleExpansionMode ? { expanded } : {}),
    ...(selection ? { rowSelection: selectedRows } : {}),
  };

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    state,
    onSortingChange: (updater) => {
      setSort((prevSort) => {
        const nextSorting =
          typeof updater === 'function' ? updater(prevSort) : updater;
        onSortingChange?.(nextSorting);
        return nextSorting;
      });
    },
    getRowId: getRowId,
    enableSorting: columns.some((column) => column.enableSorting),
    enableMultiSort: false,
    manualSorting,
    getRowCanExpand,
    getExpandedRowModel: getExpandedRowModel(),
    ...(isSingleExpansionMode
      ? { onExpandedChange: handleExpandedChange }
      : {}),
    onRowSelectionChange,
    enableRowSelection,
    enableMultiRowSelection,
  });

  const pageCount = Math.max(1, pagination?.pageCount ?? 1);
  const page = Math.max(1, pagination?.page ?? 1);
  const paginationIndex = Math.max(0, Math.min(page - 1, pageCount - 1));
  const perPageOptions =
    pagination?.perPageOptions && pagination.perPageOptions.length > 0
      ? [...pagination.perPageOptions]
      : [10, 25, 50, 100];
  const perPageValue = pagination?.perPage ?? perPageOptions[0];
  if (!perPageOptions.includes(perPageValue)) {
    perPageOptions.push(perPageValue);
    perPageOptions.sort((a, b) => a - b);
  }
  const showPagination = Boolean(pagination) && pageCount > 1;
  const showPerPage = Boolean(pagination);

  return (
    <>
      <div className={classnames({ 'table-responsive': responsive })}>
        <table className={classnames('table', className)}>
          <thead className={headerClassName}>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => {
                  const meta = header.column.columnDef.meta;
                  const align = meta?.headerAlign;
                  const customSize = meta?.customSize as boolean;
                  const canSort = header.column.getCanSort?.();
                  const sorting = header.column.getIsSorted?.(); // false | 'asc' | 'desc'
                  const headerTitle = meta?.headerTitle as string | undefined;
                  const onClick = canSort
                    ? header.column.getToggleSortingHandler()
                    : undefined;
                  const columnSize = header.getSize();
                  const style: React.CSSProperties = {
                    ...(customSize ? { width: columnSize } : {}),
                    ...(align ? { textAlign: align } : {}),
                  };
                  return (
                    <th
                      key={header.id}
                      className={classnames(meta?.headerClass, {
                        sortable: canSort,
                      })}
                      style={style}
                      onClick={onClick}
                      title={headerTitle}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={classnames(
                            canSort ? 'd-flex align-items-center' : ''
                          )}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {canSort && (
                            <span className="ms-1 d-flex" aria-hidden>
                              {sorting === 'asc' ? (
                                <SharedIcon
                                  iconName="keyboard_arrow_up"
                                  size="sm"
                                />
                              ) : sorting === 'desc' ? (
                                <SharedIcon
                                  iconName="keyboard_arrow_down"
                                  size="sm"
                                />
                              ) : (
                                <span className="ps-3"></span>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {!data || data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-muted">
                  {noDataIndication ? noDataIndication : 'No Items found'}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr className={getRowClassName?.(row)}>
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta;
                      const align = meta?.align;
                      const cellTitle =
                        typeof meta?.cellTitle === 'function'
                          ? meta.cellTitle(cell.row.original)
                          : meta?.cellTitle;
                      const cellStyleMeta = meta?.cellStyle;
                      const extraStyle =
                        typeof cellStyleMeta === 'function'
                          ? cellStyleMeta(cell.row.original)
                          : cellStyleMeta;
                      const columnSize = cell.column.getSize();
                      const style: React.CSSProperties = {
                        ...(extraStyle || {}),
                        ...(meta?.customSize ? { width: columnSize } : {}),
                        ...(align ? { textAlign: align } : {}),
                      };
                      return (
                        <td
                          key={cell.id}
                          className={classnames(meta?.cellClass)}
                          style={style}
                          title={cellTitle}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {expandedRowModel && row.getCanExpand?.() && (
                    <ExpandedRow
                      row={row}
                      colSpan={row.getVisibleCells().length}
                      className={getExpandedRowClassName?.(row)}
                      expandedRowModel={expandedRowModel}
                    />
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
          {footer && (
            <tfoot>
              <tr>
                <td colSpan={columns.length}>{footer}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {pagination && (showPagination || showPerPage) && (
        <div className="d-flex align-items-center mt-3">
          <div className="d-flex align-items-center">
            <Dropdown>
              <Dropdown.Toggle
                as="button"
                title={perPageValue.toString()}
                size="sm"
                className="button button--secondary d-flex align-items-center"
                disabled={!pagination.onPerPageChange}
              >
                {perPageValue.toString()}
                <SharedIcon iconName="arrow_drop_down" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {perPageOptions.map((option) => (
                  <Dropdown.Item
                    key={option}
                    onClick={() => pagination.onPerPageChange?.(option)}
                    active={true}
                  >
                    {option}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          {showPagination && (
            <div className="ms-auto">
              <ReactPaginate
                previousLabel={
                  <SharedIcon
                    iconName="chevron_left"
                    size="md"
                    classes="d-flex"
                  />
                }
                nextLabel={
                  <SharedIcon
                    iconName="chevron_right"
                    size="md"
                    classes="d-flex"
                  />
                }
                breakLabel={'...'}
                pageCount={pageCount}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={(sel) =>
                  pagination.onPageChange(sel.selected + 1)
                }
                forcePage={paginationIndex}
                containerClassName={'pagination mb-0 '}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}
